import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { Component } from 'react';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import Input from '@savgroup-front-common/core/atoms/Form/Input/Input';
import { InputGroup } from '@savgroup-front-common/core/atoms/Form/InputGroup';
import { Loader } from '@savgroup-front-common/core/atoms/loader';
import {
  geocodeByAddress,
  getLatLng,
  PlacesAutocomplete,
} from '@savgroup-front-common/core/atoms/ReactPlacesAutocomplete';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { PositionIcon } from '@savgroup-front-common/core/protons/icons';

import { $DropdownSuggestions, $Suggestion } from './Address.styles';
import messages from './messages';

interface UserAddress {
  addressId?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  countryCode?: string;
}

interface SearchAddress {
  address?: string;
  location?: {
    lat: number;
    lng: number;
  };
  countryCode?: string;
  city?: string | null;
  placeId?: string;
  maxDistance?: number;
}

interface AddressProps {
  searchAddress?: SearchAddress | null;
  onError?: (error: any) => void;
  userAddresses?: UserAddress[];
  onAddressSelected?: ((address: SearchAddress) => void) | null;
  isAdvancedOptionShown?: boolean;
  maxDistance: number;
}

interface AddressState {
  loading: boolean;
  placeId: string;
  isGoogleAddress: boolean;
  address?: string;
  isGettingCurrentLocation?: boolean;
}

class Address extends Component<AddressProps, AddressState> {
  static displayName = 'Address';

  static defaultProps = {
    onError: () => undefined,
    searchAddress: null,
    userAddresses: [],
    onAddressSelected: null,
    isAdvancedOptionShown: false,
  };

  state: AddressState = {
    loading: false,
    placeId: '',
    isGoogleAddress: false,
  };

  constructor(props: AddressProps) {
    super(props);
    this.state = {
      loading: false,
      placeId: '',
      isGoogleAddress: false,
    };

    if (props.searchAddress?.address) {
      this.callbackAddressSelected({
        address: props.searchAddress.address,
        countryCode: props.searchAddress.countryCode,
      });
    }
  }

  onError = (error: any) => {
    logError(error);
  };

  onSuccess = (pos: GeolocationPosition) => {
    if (window.google && window.google.maps && window.google.maps.Geocoder) {
      const crd = pos.coords;
      const latLng = { lat: crd.latitude, lng: crd.longitude };
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK') {
          const address = results?.[0];

          if (address) {
            const countryCode = get(
              find(address.address_components, (info) =>
                includes(get(info, 'types'), 'country'),
              ),
              'short_name',
            );

            this.handleOnLocalize(address.formatted_address, countryCode || '');
          } else {
            logError('No results found');
          }
        } else {
          logError(`Geocoder failed due to: ${status}`);
        }
      });
    } else {
      logError('Google Maps API not loaded');
    }
  };

  getCurrentPosition = () => {
    this.setState(() => ({ isGettingCurrentLocation: true }));
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    return new Promise<GeolocationPosition>((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject, options),
    )
      .then((data) => {
        this.setState(() => ({ isGettingCurrentLocation: false }));

        return this.onSuccess(data);
      })
      .catch((e) => {
        this.setState(() => ({ isGettingCurrentLocation: false }));

        return logError(e);
      });
  };

  handleOnLocalize = (address: string, countryCode: string) => {
    this.setState({
      address,
    });
    if (address) {
      this.callbackAddressSelected({ address, countryCode });
    }
  };

  handleChange = (address: string) => {
    this.setState({
      address,
    });
  };

  handleSelect = (
    address: string,
    placeId: string,
    isGoogleAddress?: boolean,
  ) => {
    this.setState({
      loading: true,
      placeId,
      isGoogleAddress: !!isGoogleAddress,
      address,
    });
    const { onAddressSelected, maxDistance } = this.props;

    geocodeByAddress(address)
      .then((results) => {
        getLatLng(results[0]).then(({ lat, lng }) => {
          this.setState({
            loading: false,
          });
          const country = results[0].address_components.find((c) =>
            c.types.some((t) => t === 'country'),
          );
          const city = results[0].address_components.find((c) =>
            c.types.some((t) => t === 'locality'),
          );
          const countryCode = country ? country.short_name : null;

          if (countryCode && onAddressSelected) {
            onAddressSelected({
              address,
              location: { lat, lng },
              placeId,
              countryCode,
              city: city ? city.long_name : null,
              maxDistance,
            });
          } else {
            logError('APP-MissingCountryCode', {
              countryCode,
              address,
              lat,
              lng,
            });
          }
        });
      })
      .catch(() => {
        this.setState({
          loading: false,
        });
      });
  };

  callbackAddressSelected = ({
    address,
    countryCode,
  }: {
    address: string;
    countryCode?: string;
  }) => {
    const { onAddressSelected } = this.props;

    geocodeByAddress(address)
      .then((results) => getLatLng(results[0]))
      .then((latLng) => {
        if (onAddressSelected) {
          onAddressSelected({
            address,
            location: latLng,
            countryCode,
          });
        }
      })
      // eslint-disable-next-line no-console
      .catch((error) => console.error('Error in Address UI Component', error));
  };

  render() {
    const { onError } = this.props;
    const { isGettingCurrentLocation } = this.state;

    return (
      <PlacesAutocomplete
        value={
          get(
            this.state,
            'address',
            get(this.props, ['searchAddress', 'address']),
          ) || ''
        }
        onSelect={this.handleSelect}
        onChange={this.handleChange}
        onError={onError}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <>
            <InputGroup>
              <Input
                {...getInputProps()}
                isLoading={isGettingCurrentLocation ?? false}
                selectAllOnFocus
                placeholder={messages.searchPlaces}
              />
              <Button
                primary
                hollow
                icon={<PositionIcon />}
                onClick={this.getCurrentPosition}
                type={BUTTON_TYPES.BUTTON}
                title={safeFormattedIntlString(messages.aroundMe)}
              />
            </InputGroup>

            {suggestions.length > 0 ? (
              <$DropdownSuggestions>
                {loading && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Loader />
                  </div>
                )}
                {suggestions.map((suggestion) => (
                  <$Suggestion
                    {...getSuggestionItemProps(suggestion)}
                    active={suggestion.active}
                    key={suggestion.description}
                  >
                    {suggestion.description}
                  </$Suggestion>
                ))}
              </$DropdownSuggestions>
            ) : null}
          </>
        )}
      </PlacesAutocomplete>
    );
  }
}

export default Address;
